/**
 * This file exposes frequently used image methods.
 *
 * These methods are auto-imported in components and pages and can be used
 * directly in templates, <script setup> or anywhere in the Options API without
 * having to manually import them.
 */

import {
  IMAGE_MIME_TYPE,
} from '~/constants/image'

import type { Nullable } from '~/types'

/**
 * Gets the image extension from a given mime type and file name.
 *
 * @param mimeType The mime type.
 * @param fileName The file name.
 * @returns The image extension.
 *
 * @example getImageExtensionFromMimeType('image/jpeg', 'image.jpg') // 'jpg'
 */
export function getImageExtensionFromMimeType(mimeType?: string, fileName?: string): Nullable<(typeof IMAGE_MIME_TYPE)[keyof typeof IMAGE_MIME_TYPE]> {
  const normalizedMimeType = mimeType?.toLowerCase()

  if (normalizedMimeType && normalizedMimeType in IMAGE_MIME_TYPE) {
    return IMAGE_MIME_TYPE[normalizedMimeType as keyof typeof IMAGE_MIME_TYPE]
  }

  const extensionFromFileName = fileName?.split('.').pop()?.toLowerCase()

  if (!extensionFromFileName) {
    return null
  }

  if (extensionFromFileName === 'jpeg') {
    return 'jpg'
  }

  if (Object.values(IMAGE_MIME_TYPE).includes(extensionFromFileName as IMAGE_MIME_TYPE)) {
    return extensionFromFileName as IMAGE_MIME_TYPE
  }

  return null
}

/**
 * Checks if a path is an absolute URL or not.
 *
 * @param path The path to check.
 * @returns True if the path is an absolute URL, false otherwise.
 *
 * @example isImagePathAbsolute('https://images.rvezy.com/path/photo.jpg') // true
 * @example isImagePathAbsolute('/path/photo.jpg') // false
 */
export function isImagePathAbsolute(path: string) {
  const regex = /^(?:https?:\/\/)/i

  return regex.test(path)
}
